import styled from '@emotion/styled';
import { Button, Drawer, Input, Space, Table, Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import {
  FaCheckCircle,
  FaDownload,
  FaTimesCircle,
  FaUserCheck,
} from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../entities/ConnectedUserEntity';
import { DetenteurEntity } from '../../../entities/detenteur.entity';
import { PRIMARY, YELLOW } from '../../../shared/colors';
import {
  getDetenteurAML,
  getDetenteurKYC,
} from '../../dashboard/components/Monitor/network';
import { BASE_MEDIA } from '../../shared/ApiRoutes';
import { Layout } from '../../shared/Layout';
import { exportDetenteurData } from '../clientele/components/exportDataExcel';
import { KYCForm } from '../emissionBond/components/KYCForm';
import { getDetenteurs } from '../emissionBond/network';

const Container = styled.div`
  height: calc(100vh - 30px);
  display: flex;
  background-color: #1c2025;
  color: white;

  .bread-crum span {
    color: white;
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: #757575;
    color: white;
  }

  .even-row {
    background-color: #acacac;
    color: white;
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: ${YELLOW} !important;
      cursor: pointer;
      color: black; /* plus lisible */
    }
  }

  > .content {
    padding: 50px;
    width: 100%;
    background-color: #1c2025;
    color: white;
  }
`;

export const AdminKycPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer1, setShowDrawer1] = useState(false);
  const [clients, setClients] = useState<DetenteurEntity[]>([]);
  const [filterClients, setFilterClients] = useState<DetenteurEntity[]>([]);
  const [selectedClient, setSelectedClient] = useState<DetenteurEntity>();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getDetenteurs(connectedUser.id)
      .then(async ({ data }) => {
        const tbdata: DetenteurEntity[] = await Promise.all(
          data.map(async (ligne: DetenteurEntity) => {
            const { data: kycData } = await getDetenteurKYC(ligne.id);
            const { data: amlData } = await getDetenteurAML(ligne.id);
            return {
              ...ligne,
              kyc: kycData,
              aml: amlData,
            };
          }),
        );
        console.log(tbdata);
        setClients(tbdata);
        setFilterClients(tbdata);
      })
      .finally(() => setIsLoading(false));
  };

  const search = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterClients(
      clients.filter(
        (cli) =>
          cli.nom.toLowerCase().includes(e.target.value.toLowerCase()) ||
          cli.prenom.toLowerCase().includes(e.target.value.toLowerCase()) ||
          cli.adresse.toLowerCase().includes(e.target.value.toLowerCase()),
      ),
    );
  };

  const COLUMNS = [
    {
      title: 'Nom',
      render: (cell: any, row: any) => `${row.nom} ${row.prenom}`,
    },
    {
      title: 'Statut',
      render: (cell: any, row: any) =>
        row.kyc.status ? (
          <Tag
            color={
              row.kyc.status === 'verified'
                ? 'green'
                : row.kyc.status === 'submitted'
                ? 'yellow'
                : row.kyc.status === 'pending'
                ? 'orange'
                : row.kyc.status === 'rejected'
                ? 'red'
                : 'default'
            }
          >
            {row.kyc.status.toUpperCase()}
          </Tag>
        ) : (
          <Tag color='default'>NON SOUMIS</Tag>
        ),
    },
    {
      title: 'Score AML',
      render: (cell: any, row: any) =>
        row.aml.aml_risk_level ? (
          <Tag
            color={
              row.aml.aml_risk_level === 'Faible'
                ? 'green'
                : row.aml.aml_risk_level === 'Moyen'
                ? 'yellow'
                : row.aml.aml_risk_level === 'Eleve'
                ? 'red'
                : 'default'
            }
          >
            {row.aml.aml_risk_score}
          </Tag>
        ) : (
          <Tag color='default'>N/A</Tag>
        ),
    },
    {
      title: 'Date de Naissance',
      render: (cell: any, row: any) => row.kyc.birth_date,
    },
    {
      title: 'Nationalite',
      render: (cell: any, row: any) => row.kyc.nationality,
    },
    {
      title: 'Pays de residence',
      render: (cell: any, row: any) => row.kyc.country_of_residence,
    },
    {
      title: 'Type de piece',
      render: (cell: any, row: any) => row.kyc.id_type,
    },
    {
      title: 'Numero de piece',
      render: (cell: any, row: any) => row.agence.id_number,
    },
    {
      title: 'Adresse',
      render: (cell: any, row: any) => row.kyc.address,
    },
    {
      title: 'Profession',
      render: (cell: any, row: any) => row.kyc.employment_status,
    },
    {
      title: "Objectif d'investissement",
      render: (cell: any, row: any) => row.kyc.investment_purpose,
    },
    {
      title: 'PPE?',
      render: (cell: any, row: any) =>
        row.kyc.politically_exposed ? 'Oui' : 'Non',
    },
    {
      title: 'Revenue',
      render: (cell: any, row: any) => row.kyc.income_level,
    },
    {
      title: 'Document Recto?',
      render: (cell: any, row: any) =>
        row.kyc.id_document_front ? (
          <a
            href={BASE_MEDIA + row.kyc.id_document_front}
            target='_blank'
            rel='noopener noreferrer'
          >
            <FaCheckCircle size={20} color='green' />
          </a>
        ) : (
          <Space>
            <FaTimesCircle size={20} color='red' />
          </Space>
        ),
    },
    {
      title: 'Document verso?',
      render: (cell: any, row: any) =>
        row.kyc.id_document_back ? (
          <a
            href={BASE_MEDIA + row.kyc.id_document_back}
            target='_blank'
            rel='noopener noreferrer'
          >
            <FaCheckCircle size={20} color='green' />
          </a>
        ) : (
          <Space>
            <FaTimesCircle size={20} color='red' />
          </Space>
        ),
    },
    {
      title: 'Selfie?',
      render: (cell: any, row: any) =>
        row.kyc.selfie ? (
          <a
            href={BASE_MEDIA + row.kyc.selfie}
            target='_blank'
            rel='noopener noreferrer'
          >
            <FaCheckCircle size={20} color='green' />
          </a>
        ) : (
          <Space>
            <FaTimesCircle size={20} color='red' />
          </Space>
        ),
    },
    {
      title: 'Jusif Domicile?',
      render: (cell: any, row: any) =>
        row.kyc.proof_of_residency ? (
          <a
            href={BASE_MEDIA + row.kyc.proof_of_residency}
            target='_blank'
            rel='noopener noreferrer'
          >
            <FaCheckCircle size={20} color='green' />
          </a>
        ) : (
          <Space>
            <FaTimesCircle size={20} color='red' />
          </Space>
        ),
    },
    {
      title: 'Justif Revenue',
      render: (cell: any, row: any) =>
        row.kyc.proof_of_revenue ? (
          <a
            href={BASE_MEDIA + row.kyc.proof_of_revenue}
            target='_blank'
            rel='noopener noreferrer'
          >
            <FaCheckCircle size={20} color='green' />
          </a>
        ) : (
          <Space>
            <FaTimesCircle size={20} color='red' />
          </Space>
        ),
    },
    {
      title: 'Actions',
      render: (cell: any, row: any) => (
        <Space>
          <Button
            icon={<FaUserCheck style={{ color: 'black', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: PRIMARY,
            }}
            onClick={() => {
              setSelectedClient(row);
              setShowDrawer1(true);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <div>
            <h2 style={{ marginTop: 20, color: 'white' }}>Gestion des KYCs</h2>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 5,
              }}
            >
              <Space>
                <Input
                  placeholder='Search...'
                  style={{ width: 250 }}
                  onChange={search}
                />
                <Tooltip title='Rafraichir' placement='top' color={YELLOW}>
                  <span style={{ cursor: 'pointer' }} onClick={fetchData}>
                    <FiRefreshCcw color='black' />
                  </span>
                </Tooltip>
                <PulseLoader
                  size={5}
                  margin={2}
                  color={YELLOW}
                  loading={isLoading}
                />
              </Space>

              <Space>
                <Button
                  icon={
                    <FaDownload style={{ marginBottom: -2, marginRight: 5 }} />
                  }
                  className='butt'
                  style={{ borderRadius: 5 }}
                  onClick={() => exportDetenteurData(clients)}
                >
                  Exporter
                </Button>
              </Space>
            </div>

            <Table
              columns={COLUMNS}
              dataSource={filterClients}
              scroll={{ x: 'max-content' }}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              rowKey='id'
              size='small'
              loading={isLoading}
              style={{ zoom: 0.9 }}
              pagination={{ pageSize: 10 }}
              onRow={(row, rowIndex) => {
                return {
                  onClick: (event) => {}, // click row
                  onDoubleClick: (event) => {}, // double click row
                  onContextMenu: (event) => {}, // right button click row
                  onMouseEnter: (event) => {}, // mouse enter row
                  onMouseLeave: (event) => {}, // mouse leave row
                };
              }}
            />
          </div>
        </div>
        <Drawer
          title='Verifier le KYC'
          placement='right'
          width={400}
          onClose={() => setShowDrawer1(false)}
          open={showDrawer1}
          closeIcon={<MdClose size={20} />}
        >
          <KYCForm
            clientToUpdate={selectedClient}
            setShowDrawer={setShowDrawer1}
            getNewClient={(cli) => {
              setClients([...clients, cli]);
              setFilterClients([...filterClients, cli]);
            }}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};
